import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { WIDGET_METADATA, getDefaultLayout, getAvailableTemplates } from '../config/defaultColumnLayouts';
import layoutService from '../services/layoutService';
import {
  LayoutDashboard, Briefcase, GraduationCap, Coins, MapPin, FileText,
  History, Phone, Settings, Tag, LayoutGrid,
  Building2, ListChecks, Target, Sparkles, StickyNote,
  MessageSquare, GitBranch, Cpu
} from 'lucide-react';

// Map the WIDGET_METADATA icon name → Lucide component. Falls back to a
// generic grid icon so an unknown/missing name never crashes the header.
const WIDGET_ICONS = {
  LayoutDashboard, Briefcase, GraduationCap, Coins, MapPin, FileText,
  History, Phone, Settings, Tag,
  Building2, ListChecks, Target, Sparkles, StickyNote,
  MessageSquare, GitBranch, Cpu,
};

// One accent color for every widget header chip (emerald, matching the app scheme).
// Light and dark variants — the chip uses inline styles (not Tailwind classes),
// so theme.css can't override it; we pick the variant from the active theme.
const HEADER_ACCENT_LIGHT = '#047857';      // emerald-700
const HEADER_ACCENT_TINT_LIGHT = '#D1FAE5'; // emerald-100
const HEADER_ACCENT_DARK = '#5ec898';       // muted light-green (matches theme.css dark emerald text)
const HEADER_ACCENT_TINT_DARK = '#24402f';  // dark emerald surface (matches theme.css bg-emerald-100 dark)

// Renders the tinted icon chip for a widget header.
const WidgetHeaderIcon = ({ widgetId }) => {
  const name = WIDGET_METADATA[widgetId]?.icon;
  const Icon = WIDGET_ICONS[name] || LayoutGrid;
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark';
  const accent = isDark ? HEADER_ACCENT_DARK : HEADER_ACCENT_LIGHT;
  const tint = isDark ? HEADER_ACCENT_TINT_DARK : HEADER_ACCENT_TINT_LIGHT;
  return (
    <span
      className="flex items-center justify-center flex-shrink-0"
      style={{ width: 22, height: 22, borderRadius: 6, backgroundColor: tint }}
    >
      <Icon size={14} style={{ color: accent }} strokeWidth={2} />
    </span>
  );
};

/**
 * ColumnBasedLayout Component
 * 
 * True column-based layout where widgets snap to predefined columns
 * Responsive breakpoints (based on container/viewport width):
 * - 4 cols: 1400px+ (large desktop)
 * - 3 cols: 1100px - 1399px (desktop/tablet landscape)
 * - 2 cols: 700px - 1099px (tablet portrait)
 * - 1 col: < 700px (mobile)
 * 
 * Edit mode automatically detects current breakpoint and saves to that breakpoint
 * 
 * Props:
 * - containerWidth: Optional. If provided, uses this width for breakpoint calculation
 *                   instead of window.innerWidth. Useful for panels/overlays.
 */

const BREAKPOINT_NAMES = {
  4: 'Desktop (4 columns)',
  3: 'Tablet Landscape (3 columns)',
  2: 'Tablet Portrait (2 columns)',
  1: 'Mobile (1 column)'
};

const ColumnBasedLayout = ({ pageType, widgets, widgetProps = {}, onEditModeChange, containerWidth }) => {
  // Store layouts for each breakpoint: { 4: {...}, 3: {...}, 2: {...}, 1: {...} }
  const [breakpointLayouts, setBreakpointLayouts] = useState({});
  const [hiddenWidgets, setHiddenWidgets] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [visibleColumnCount, setVisibleColumnCount] = useState(4);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const containerRef = useRef(null);
  const breakpointLayoutsRef = useRef(breakpointLayouts);
  const hiddenWidgetsRef = useRef(hiddenWidgets);

  // Keep refs in sync with state so handleSave always reads latest values
  useEffect(() => { breakpointLayoutsRef.current = breakpointLayouts; }, [breakpointLayouts]);
  useEffect(() => { hiddenWidgetsRef.current = hiddenWidgets; }, [hiddenWidgets]);

  // Calculate column count based on width
  const calculateColumnCount = (width) => {
    if (width < 700) return 1;
    if (width < 1100) return 2;
    if (width < 1400) return 3;
    return 4;
  };

  // Track container/screen size for responsive columns
  useEffect(() => {
    const updateColumnCount = () => {
      // Use containerWidth prop if provided, otherwise measure container or use window
      let width;
      if (containerWidth !== undefined) {
        width = containerWidth;
      } else if (containerRef.current) {
        width = containerRef.current.offsetWidth;
      } else {
        width = window.innerWidth;
      }
      setVisibleColumnCount(calculateColumnCount(width));
    };

    updateColumnCount();
    window.addEventListener('resize', updateColumnCount);
    return () => window.removeEventListener('resize', updateColumnCount);
  }, [containerWidth]);

  // Update when containerWidth changes
  useEffect(() => {
    if (containerWidth !== undefined) {
      setVisibleColumnCount(calculateColumnCount(containerWidth));
    }
  }, [containerWidth]);

  // Notify parent of edit mode changes
  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange({
        isEditMode,
        hasChanges,
        isSaving,
        hiddenWidgetsCount: hiddenWidgets.length,
        onSave: handleSave,
        onCancel: handleCancel,
        onReset: handleReset,
        setIsEditMode
      });
    }
  }, [isEditMode, hasChanges, isSaving, hiddenWidgets.length]);

  useEffect(() => {
    loadLayout();
    loadSavedTemplates();
  }, [pageType]);

  const loadLayout = async () => {
    try {
      const userLayout = await layoutService.getUserLayout(pageType);
      
      if (userLayout) {
        // Check if new format (breakpoint-specific) or old format
        if (userLayout.breakpointLayouts) {
          setBreakpointLayouts(reconcileBreakpointLayouts(userLayout.breakpointLayouts, userLayout.hiddenWidgets || []));
          setHiddenWidgets(userLayout.hiddenWidgets || []);
        } else if (userLayout.columns) {
          // Old format - create breakpoint layouts from it
          const bpLayouts = createDefaultBreakpointLayouts(userLayout.columns);
          setBreakpointLayouts(reconcileBreakpointLayouts(bpLayouts, userLayout.hiddenWidgets || []));
          setHiddenWidgets(userLayout.hiddenWidgets || []);
        } else if (userLayout.layouts) {
          // Handle nested layouts format
          if (userLayout.layouts.breakpointLayouts) {
            setBreakpointLayouts(reconcileBreakpointLayouts(userLayout.layouts.breakpointLayouts, userLayout.layouts.hiddenWidgets || []));
            setHiddenWidgets(userLayout.layouts.hiddenWidgets || []);
          } else if (userLayout.layouts.columns) {
            const bpLayouts = createDefaultBreakpointLayouts(userLayout.layouts.columns);
            setBreakpointLayouts(reconcileBreakpointLayouts(bpLayouts, userLayout.layouts.hiddenWidgets || []));
            setHiddenWidgets(userLayout.layouts.hiddenWidgets || []);
          } else {
            setBreakpointLayouts(createDefaultBreakpointLayouts());
            setHiddenWidgets([]);
          }
        } else {
          setBreakpointLayouts(createDefaultBreakpointLayouts());
          setHiddenWidgets([]);
        }
      } else {
        setBreakpointLayouts(createDefaultBreakpointLayouts());
        setHiddenWidgets([]);
      }
    } catch (error) {
      console.error('Error loading layout:', error);
      setBreakpointLayouts(createDefaultBreakpointLayouts());
      setHiddenWidgets([]);
    }
  };

  const loadSavedTemplates = async () => {
    try {
      const templates = await layoutService.getSavedTemplates(pageType);
      setSavedTemplates(templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      setSavedTemplates([]);
    }
  };

  // Create default layouts for each breakpoint
  // Reconcile a loaded/saved layout against the currently-registered widgets:
  // any widget that exists in `widgets` but isn't placed in a column (and isn't
  // hidden) gets appended so newly-added widgets always appear. This never
  // removes or reorders what the user saved — it only adds the missing ones.
  const reconcileBreakpointLayouts = (bpLayouts, hidden = []) => {
    const registered = Object.keys(widgets || {});
    const next = JSON.parse(JSON.stringify(bpLayouts || {}));
    Object.keys(next).forEach(bp => {
      const cols = next[bp]?.columns || [];
      if (cols.length === 0) return;
      const placed = new Set(cols.flatMap(c => c.widgetIds || []));
      const missing = registered.filter(id => !placed.has(id) && !hidden.includes(id));
      if (missing.length > 0) {
        // Append missing widgets to the shortest column to keep things balanced.
        const shortest = cols.reduce((a, b) => ((a.widgetIds || []).length <= (b.widgetIds || []).length ? a : b), cols[0]);
        shortest.widgetIds = [...(shortest.widgetIds || []), ...missing];
      }
    });
    return next;
  };

  const createDefaultBreakpointLayouts = (baseColumns = null) => {
    const defaultCols = baseColumns || getDefaultLayout(pageType, 'default') || getDefaultColumns();
    
    // Ensure we have 4 columns
    const cols4 = JSON.parse(JSON.stringify(defaultCols));
    while (cols4.length < 4) {
      cols4.push({ id: `col-${cols4.length + 1}`, widgetIds: [] });
    }
    
    // For 3 columns, merge column 4 into column 3
    const cols3 = JSON.parse(JSON.stringify(cols4.slice(0, 3)));
    if (cols4[3]?.widgetIds?.length > 0) {
      cols3[2].widgetIds = [...cols3[2].widgetIds, ...cols4[3].widgetIds];
    }
    
    // For 2 columns, merge appropriately
    const cols2 = [
      { id: 'col-1', widgetIds: [...(cols4[0]?.widgetIds || []), ...(cols4[2]?.widgetIds || [])] },
      { id: 'col-2', widgetIds: [...(cols4[1]?.widgetIds || []), ...(cols4[3]?.widgetIds || [])] }
    ];
    
    // For 1 column, put all widgets in single column
    const allWidgets = cols4.flatMap(col => col.widgetIds || []);
    const cols1 = [{ id: 'col-1', widgetIds: allWidgets }];
    
    return {
      4: { columns: cols4 },
      3: { columns: cols3 },
      2: { columns: cols2 },
      1: { columns: cols1 }
    };
  };

  const getDefaultColumns = () => {
    const defaultLayout = getDefaultLayout(pageType, 'default');
    if (defaultLayout) return defaultLayout;
    
    // Fallback
    const allWidgetIds = Object.keys(widgets);
    return [
      { id: 'col-1', widgetIds: allWidgetIds },
      { id: 'col-2', widgetIds: [] },
      { id: 'col-3', widgetIds: [] },
      { id: 'col-4', widgetIds: [] }
    ];
  };

  // Get current columns for the current breakpoint
  const currentColumns = useMemo(() => {
    const layout = breakpointLayouts[visibleColumnCount];
    if (layout?.columns && layout.columns.length > 0) {
      return layout.columns;
    }
    // Fallback: create default columns for this breakpoint
    const defaultCols = [];
    for (let i = 0; i < visibleColumnCount; i++) {
      defaultCols.push({ id: `col-${i + 1}`, widgetIds: [] });
    }
    return defaultCols;
  }, [breakpointLayouts, visibleColumnCount]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const newColumns = JSON.parse(JSON.stringify(currentColumns));
    const sourceColIndex = parseInt(source.droppableId.split('-')[1]) - 1;
    const destColIndex = parseInt(destination.droppableId.split('-')[1]) - 1;

    if (!newColumns[sourceColIndex] || !newColumns[destColIndex]) return;

    const sourceCol = newColumns[sourceColIndex];
    const destCol = newColumns[destColIndex];

    const [movedWidget] = sourceCol.widgetIds.splice(source.index, 1);
    destCol.widgetIds.splice(destination.index, 0, movedWidget);

    // Update the current breakpoint layout
    const nextBreakpointLayouts = {
      ...breakpointLayoutsRef.current,
      [visibleColumnCount]: { columns: newColumns }
    };
    setBreakpointLayouts(nextBreakpointLayouts);

    if (isEditMode) {
      // In edit mode moves are batched and committed via the Save button.
      setHasChanges(true);
    } else {
      // Always-on repositioning (outside edit mode) persists immediately, so
      // the user can rearrange widgets any time without entering edit mode.
      // Hidden widgets are untouched here — visibility stays an edit-mode action.
      layoutService.saveUserLayout(pageType, {
        breakpointLayouts: nextBreakpointLayouts,
        hiddenWidgets: hiddenWidgetsRef.current
      }).catch(err => console.error('Auto-save layout failed:', err));
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Use refs to get the LATEST state, not stale closure values
      await layoutService.saveUserLayout(pageType, { 
        breakpointLayouts: breakpointLayoutsRef.current, 
        hiddenWidgets: hiddenWidgetsRef.current 
      });
      
      setHasChanges(false);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving layout:', error);
      alert('Failed to save layout');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default layout? This will reset all breakpoint layouts.')) {
      return;
    }

    try {
      await layoutService.resetUserLayout(pageType);
      setBreakpointLayouts(createDefaultBreakpointLayouts());
      setHiddenWidgets([]);
      setHasChanges(false);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error resetting layout:', error);
      alert('Failed to reset layout');
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    setIsEditMode(false);
    setHasChanges(false);
    loadLayout();
  };

  const toggleWidgetVisibility = (widgetId) => {
    setHiddenWidgets(prev => {
      if (prev.includes(widgetId)) {
        return prev.filter(id => id !== widgetId);
      } else {
        return [...prev, widgetId];
      }
    });
    setHasChanges(true);
  };

  // Save as template
  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return;

    try {
      await layoutService.saveTemplate(pageType, newTemplateName, {
        breakpointLayouts: breakpointLayoutsRef.current,
        hiddenWidgets: hiddenWidgetsRef.current
      });
      loadSavedTemplates();
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
      alert('Template saved!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  // Load template
  const handleLoadTemplate = (template) => {
    if (template.data?.breakpointLayouts) {
      setBreakpointLayouts(template.data.breakpointLayouts);
      setHiddenWidgets(template.data.hiddenWidgets || []);
    } else if (template.data?.columns) {
      setBreakpointLayouts(createDefaultBreakpointLayouts(template.data.columns));
      setHiddenWidgets(template.data.hiddenWidgets || []);
    }
    setHasChanges(true);
    setShowTemplateModal(false);
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      await layoutService.deleteTemplate(templateId);
      loadSavedTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Edit Mode Controls - Simplified */}
      {isEditMode && (
        <div className="mb-3 bg-white border rounded-lg p-3 shadow-sm space-y-3">
          {/* Current Breakpoint Info */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Editing:</span>
              <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded font-medium">
                {BREAKPOINT_NAMES[visibleColumnCount]}
              </span>
            </div>
            
            {/* Template Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTemplateModal(true)}
                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
              >
                📥 Load Template
              </button>
              <button
                onClick={() => setShowSaveTemplateModal(true)}
                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                💾 Save Template
              </button>
            </div>
          </div>

          {/* Widget Visibility */}
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-gray-600 mb-2">Widget Visibility:</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(widgets).map(widgetId => (
                <button
                  key={widgetId}
                  onClick={() => toggleWidgetVisibility(widgetId)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    hiddenWidgets.includes(widgetId)
                      ? 'bg-gray-200 text-gray-500 line-through'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                  title={hiddenWidgets.includes(widgetId) ? 'Click to show' : 'Click to hide'}
                >
                  {hiddenWidgets.includes(widgetId) ? '👁️‍🗨️' : '👁️'} 
                  {' '}{WIDGET_METADATA[widgetId]?.title || widgetId}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-xs text-gray-400 border-t pt-2">
            Tip: Resize window to edit layouts for different screen sizes. Changes save to the current view.
          </p>
        </div>
      )}

      {/* Column Layout */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={`grid gap-3 ${
          visibleColumnCount === 1 ? 'grid-cols-1' :
          visibleColumnCount === 2 ? 'grid-cols-2' :
          visibleColumnCount === 3 ? 'grid-cols-3' :
          'grid-cols-4'
        }`}>
          {currentColumns.map((column, colIndex) => (
            <Droppable key={column.id} droppableId={column.id} isDropDisabled={false}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-[200px] sm:min-h-[400px] rounded p-1.5 transition-colors ${
                    snapshot.isDraggingOver
                      ? 'bg-emerald-50 border-2 border-emerald-400 border-dashed'
                      : isEditMode
                      ? 'bg-gray-50 border-2 border-gray-300 border-dashed'
                      : 'bg-transparent'
                  }`}
                >
                  {isEditMode && (
                    <div className="text-xs text-gray-500 mb-1.5 text-center font-medium">
                      Col {colIndex + 1}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {(column.widgetIds || [])
                      .filter(widgetId => !hiddenWidgets.includes(widgetId))
                      .map((widgetId, index) => {
                        const WidgetComponent = widgets[widgetId];
                        if (!WidgetComponent) return null;

                        return (
                          <Draggable
                            key={widgetId}
                            draggableId={widgetId}
                            index={index}
                            isDragDisabled={false}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-white rounded shadow ${
                                  snapshot.isDragging ? 'opacity-50 rotate-2' : ''
                                } ${isEditMode ? 'ring-1 ring-emerald-200' : ''}`}
                              >
                                {/* Widget Header — always a drag handle now, so
                                    widgets can be repositioned without entering
                                    edit mode. The grip icon signals this. */}
                                <div
                                  {...provided.dragHandleProps}
                                  className={`flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t cursor-move ${
                                    isEditMode ? 'hover:bg-gray-100' : 'hover:bg-gray-100/70'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <WidgetHeaderIcon widgetId={widgetId} />
                                    <h3 className="font-medium text-sm text-gray-900 truncate">
                                      {WIDGET_METADATA[widgetId]?.title || widgetId}
                                    </h3>
                                  </div>
                                  {/* Move affordance — right-aligned. 4-way arrows
                                      literally signal "drag to move". */}
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    {isEditMode && (
                                      <span className="text-xs text-gray-400">Drag</span>
                                    )}
                                    <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                      <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                                    </svg>
                                  </div>
                                </div>

                                {/* Widget Content */}
                                <div className="p-3">
                                  <WidgetComponent {...widgetProps} />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </div>

                  {isEditMode && (column.widgetIds || []).filter(id => !hiddenWidgets.includes(id)).length === 0 && (
                    <div className="text-center py-12 text-gray-400 text-sm">
                      Drop widgets here
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Load Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">Load Layout Template</h3>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Built-in Templates */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 mb-2">Built-in Templates</div>
                {getAvailableTemplates(pageType).map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      const defaultCols = getDefaultLayout(pageType, template.id);
                      if (defaultCols) {
                        setBreakpointLayouts(createDefaultBreakpointLayouts(defaultCols));
                        setHasChanges(true);
                      }
                      setShowTemplateModal(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-medium">{template.name}</div>
                      <div className="text-xs text-gray-500">{template.description}</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Saved Templates */}
              {savedTemplates.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">Your Templates</div>
                  {savedTemplates.map(template => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded"
                    >
                      <button
                        onClick={() => handleLoadTemplate(template)}
                        className="text-left flex-1"
                      >
                        <div className="text-sm font-medium">{template.name}</div>
                        <div className="text-xs text-gray-500">
                          Saved {new Date(template.created_at).toLocaleDateString()}
                        </div>
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {savedTemplates.length === 0 && (
                <div className="text-center py-4 text-gray-400 text-sm">
                  No saved templates yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-80 p-4">
            <h3 className="font-semibold mb-3">Save as Template</h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Template name..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-3"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
            />
            <p className="text-xs text-gray-500 mb-4">
              Saves layouts for all screen sizes and widget visibility.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowSaveTemplateModal(false); setNewTemplateName(''); }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim()}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnBasedLayout;
